<script>
  import { onMount } from 'svelte';
  import { apiFetch, isLoggedIn} from '../lib/auth.js';
  import { createEventDispatcher } from 'svelte';
  import Markdown from '../components/Markdown.svelte';
  import ReviewForm from '../components/ReviewForm.svelte';

  const dispatch = createEventDispatcher();

  export let refreshTrigger = 0;
  export let loggedIn = false;

  let reviews      = [];
  let loading      = false;
  let error        = '';

  // ── User statistics ───────────────────────────────────────────────────
  let stats        = null;
  let statsLoading = false;

  // ── Inline expanded row ───────────────────────────────────────────────
  let expandedReview = null; // review being edited inline
  let creatingNew    = false; // new log row visible at top

  // ─────────────────────────────────────────────────────────────────────
  // Load
  // ─────────────────────────────────────────────────────────────────────


  async function loadReviews() {
    if (!$isLoggedIn) { reviews = []; stats = null; return; }
    loading = true; error = '';
    try {
      const data = await apiFetch('/reviews');
      reviews = Array.isArray(data) ? data : (data.reviews || []);
    } catch (e) {
      error = e.message || 'Failed to load reviews';
    } finally {
      loading = false;
    }
    statsLoading = true;
    try {
      stats = await apiFetch('/statistics/user');
    } catch {
      stats = null;
    } finally {
      statsLoading = false;
    }
  }

  onMount(() => {
    loadReviews();
  });

  $: if (refreshTrigger > 0) {
    loadReviews();
  }

  // ─────────────────────────────────────────────────────────────────────
  // Expand / collapse
  // ─────────────────────────────────────────────────────────────────────
  function toggleExpand(r) {
    if (expandedReview?.id === r.id) {
      expandedReview = null;
    } else {
      creatingNew    = false;
      expandedReview = r;
    }
  }

  function openCreate() {
    if (!loggedIn) { dispatch('login'); return; }
    expandedReview = null;
    creatingNew    = true;
  }

  function onSaved() {
    expandedReview = null;
    creatingNew    = false;
    loadReviews();
  }

  // ─────────────────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────────────────
  function formatDate(dateStr) {
    if (!dateStr) return '—';
    const [y, m, d] = dateStr.split('-');
    return `${d}.${m}.${y}`;
  }

  function ratingColour(n) {
    if (n >= 8) return '#6db800';
    if (n >= 5) return '#8a7000';
    return '#663300';
  }
</script>

<div class="reviews-root">

  <!-- ── Page header ── -->
  <div class="page-header">
    <h2 class="page-title">My Reviews</h2>
    <button class="add-btn" on:click={openCreate} class:active={creatingNew}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
      </svg>
      {loggedIn ? 'Log Show' : 'Login to Log'}
    </button>
  </div>

  <!-- ── Stats bar ── -->
  {#if stats || statsLoading}
    <div class="stats-bar">
      {#if statsLoading}
        <span class="stats-loading"><span class="spinner-sm"></span></span>
      {:else}
        <div class="stat">
          <span class="stat-value">{stats.total_reviews}</span>
          <span class="stat-label">Total</span>
        </div>
        <div class="stat-div"></div>
        <div class="stat">
          <span class="stat-value">{stats.total_reviews_current_year}</span>
          <span class="stat-label">This Year</span>
        </div>
        <div class="stat-div"></div>
        <div class="stat">
          <span class="stat-value">{stats.unique_bands_reviewed}</span>
          <span class="stat-label">Artists</span>
        </div>
        <div class="stat-div"></div>
        <div class="stat">
          <span class="stat-value">{stats.average_rating.toFixed(1)}</span>
          <span class="stat-label">Avg Rating</span>
        </div>
      {/if}
    </div>
  {/if}

  <!-- ── Review list ── -->
  {#if loading}
    <div class="state-msg"><span class="spinner"></span> Loading...</div>
  {:else if error}
    <div class="state-error">{error}</div>
  {:else}

    <!-- ── Inline create form (always available) ── -->
    {#if creatingNew}
      <div class="create-form-wrap">
        <ReviewForm
          mode="create"
          inline={true}
          on:logged={() => { creatingNew = false; loadReviews(); }}
          on:cancel={() => creatingNew = false}
        />
      </div>
    {/if}

    {#if reviews.length === 0}
      {#if !creatingNew}
        <div class="state-msg empty">No reviews yet. Log your first show.</div>
      {/if}
    {:else}
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th class="col-chevron"></th>
              <th>Artist</th>
              <th>Date</th>
              <th class="col-hide-sm">Venue</th>
              <th class="col-rating">Rating</th>
            </tr>
          </thead>
          <tbody>
            {#each reviews as r (r.id)}

            <!-- ── Summary row ── -->
            <tr
              class="summary-row"
              class:expanded={expandedReview?.id === r.id}
              class:unverified={!r.is_public}
              on:click={() => toggleExpand(r)}
              role="button"
              tabindex="0"
              on:keydown={e => e.key === 'Enter' && toggleExpand(r)}
            >
              <td class="cell-chevron">
                <svg
                  class="chevron"
                  class:open={expandedReview?.id === r.id}
                  viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                >
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </td>
              <td class="cell-artist">
                {r.artist_name || '—'} {#if r.artist_country}<span class="cell-artist-country">({r.artist_country})</span>{/if}
                {#if !r.is_public}
                  <span class="badge-unverified" title="Private — performance not yet verified">~</span>
                {/if}
              </td>
              <td class="cell-date">{formatDate(r.performance_date)}</td>
              <td class="cell-venue col-hide-sm">{r.venue_name || '—'}</td>
              <td class="cell-rating">
                <span class="rating-pill" style="color:{ratingColour(r.rating)};border-color:{ratingColour(r.rating)}55">
                  {r.rating}/10
                </span>
              </td>
            </tr>

            <!-- ── Inline expanded edit row ── -->
            {#if expandedReview?.id === r.id}
              <tr class="expand-row">
                <td colspan="5" class="expand-cell">
                  <div class="expand-inner">

                    <!-- Read-only headline/body preview above the form -->
                    {#if r.headline || r.body}
                      <div class="read-section">
                        {#if r.headline}
                          <p class="read-headline">"{r.headline}"</p>
                        {/if}
                        {#if r.body}
                          <div class="read-body"><Markdown content={r.body} /></div>
                        {/if}
                        <div class="read-divider"></div>
                      </div>
                    {/if}

                    <!-- ReviewForm handles all edit state internally -->
                    <ReviewForm
                      mode="edit"
                      inline={true}
                      review={r}
                      on:saved={() => onSaved(r)}
                      on:deleted={() => { reviews = reviews.filter(rev => rev.id !== r.id); expandedReview = null; loadReviews(); }}
                      on:cancel={() => expandedReview = null}
                    />

                  </div>
                </td>
              </tr>
            {/if}

          {/each}
        </tbody>
      </table>
    </div>
    {/if}
  {/if}

</div>

<style>
  @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;600;700&display=swap');

  /* ── Page header ── */
  .page-header {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 1rem;
  }
  .page-title {
    font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.7rem; color: #6db800;
    letter-spacing: 0.25em; text-transform: uppercase;
    border-left: 3px solid #3a6b00; padding-left: 0.75rem;
  }
  .add-btn {
    display: flex; align-items: center; gap: 0.3rem;
    background: #1a3300; border: 1px solid #3a6b00; color: #6db800;
    font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.6rem;
    letter-spacing: 0.2em; text-transform: uppercase;
    padding: 0.45rem 0.8rem; border-radius: 2px; cursor: pointer;
    transition: background 0.2s, box-shadow 0.2s;
  }
  .add-btn:hover, .add-btn.active { background: #223d00; box-shadow: 0 0 8px rgba(109,184,0,0.3); }
  .add-btn svg { width: 14px; height: 14px; }

  .create-form-wrap {
    border: 1px solid #1e3d00; border-radius: 2px; margin-bottom: 0.75rem; overflow: hidden;
  }

  /* ── Create row variant ── */
  .expand-row--create { border-top: 1px solid #2a5200; }
  .stats-bar {
    display: flex; align-items: center; justify-content: space-between;
    background: #0f0f0f; border: 1px solid #1e3d00; border-radius: 4px;
    padding: 0.65rem 1rem; margin-bottom: 1rem;
    box-shadow: inset 0 0 20px rgba(74,124,0,0.04);
  }
  .stat { display: flex; flex-direction: column; align-items: center; gap: 0.1rem; }
  .stat-value {
    font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 1.3rem;
    line-height: 1; color: #6db800; text-shadow: 0 0 8px rgba(109,184,0,0.5); letter-spacing: 0.05em;
  }
  .stat-label {
    font-family: 'Rajdhani', sans-serif; font-weight: 600; font-size: 0.5rem;
    letter-spacing: 0.2em; text-transform: uppercase; color: #3a6b00;
  }
  .stat-div { width: 1px; height: 1.75rem; background: linear-gradient(180deg, transparent, #2a5200, transparent); }
  .stats-loading { display: flex; align-items: center; padding: 0.4rem 0; }

  /* ── States ── */
  .state-msg {
    display: flex; align-items: center; gap: 0.6rem; color: #2a5200;
    font-family: 'Rajdhani', sans-serif; font-size: 0.7rem;
    letter-spacing: 0.15em; text-transform: uppercase; padding: 2rem 0;
  }
  .state-msg.empty { color: #1e3d00; }
  .state-error { color: #cc6633; font-family: 'Rajdhani', sans-serif; font-size: 0.65rem; padding: 1rem 0; }
  .spinner {
    width: 14px; height: 14px; flex-shrink: 0;
    border: 2px solid #1e3d00; border-top-color: #6db800;
    border-radius: 50%; animation: spin 0.7s linear infinite;
  }
  .spinner-sm {
    width: 14px; height: 14px; border: 2px solid #1e3d00; border-top-color: #6db800;
    border-radius: 50%; animation: spin 0.7s linear infinite; display: block; flex-shrink: 0;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* ── Table ── */
  .table-wrap { overflow: hidden; border: 1px solid #1e3d00; border-radius: 2px; }
  table { width: 100%; table-layout: fixed; border-collapse: collapse; font-family: 'Rajdhani', sans-serif; }
  thead tr { background: #0d1a00; border-bottom: 1px solid #1e3d00; }
  th {
    padding: 0.55rem 0.75rem; font-size: 0.5rem; font-weight: 700;
    letter-spacing: 0.25em; text-transform: uppercase; color: #3a6b00;
    text-align: left; white-space: nowrap;
  }
  .col-chevron { width: 28px; padding: 0; }
  .col-rating  { width: 70px; text-align: right; }

  /* ── Summary rows ── */
  .summary-row { border-bottom: 1px solid #0f1a00; cursor: pointer; transition: background 0.1s; }
  .summary-row:hover { background: #0d1a00; }
  .summary-row.expanded { background: #0d1a00; border-bottom: none; }
  .summary-row:focus-visible { outline: 1px solid #3a6b00; outline-offset: -1px; }
  .summary-row.unverified { opacity: 0.65; }

  td { padding: 0.6rem 0.75rem; font-size: 0.85rem; font-weight: 600; color: #c8c8c8; vertical-align: middle; }

  .cell-chevron { padding: 0 0 0 0.6rem; width: 28px; }
  .chevron { width: 16px; height: 16px; stroke: #2a5200; display: block; transition: transform 0.2s ease; }
  .chevron.open { transform: rotate(180deg); }

  .cell-artist { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 0; }
  .cell-artist-country { font-weight: 600; font-size: 0.8rem; color: #3a6b00; letter-spacing: 0.05em; }
  .badge-unverified {
    font-size: 0.55rem; font-weight: 700; color: #8a7000;
    border: 1px solid rgba(150,120,0,0.35); border-radius: 2px; padding: 0 3px;
  }
  .cell-date { color: #4a7c00; font-size: 0.75rem; white-space: nowrap; letter-spacing: 0.05em; }
  .cell-venue { color: #888; font-size: 0.8rem; max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .cell-rating { text-align: right; }
  .rating-pill {
    display: inline-block; border: 1px solid; border-radius: 2px;
    font-size: 0.75rem; font-weight: 700; padding: 0.15rem 0.5rem; letter-spacing: 0.05em;
  }

  @media (max-width: 480px) { .col-hide-sm { display: none; } }

  /* ── Expanded row ── */
  .expand-row { border-bottom: 1px solid #1e3d00; }
  .expand-cell { padding: 0 !important; background: #080f00; }

  .expand-inner {
    padding: 1rem 1rem 1rem 2.5rem;
    animation: slideDown 0.15s ease;
  }
  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-4px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* ── Read-only preview ── */
  .read-section { margin-bottom: 1rem; }
  .read-headline {
    font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.95rem;
    color: #c8c8c8; font-style: italic; letter-spacing: 0.03em;
    border-left: 2px solid #3a6b00; padding-left: 0.65rem; margin-bottom: 0.5rem;
  }
  .read-body { padding-left: 0.65rem; }
  .read-divider { border-top: 1px solid #1e3d00; margin-top: 0.85rem; }
</style>
